import type { KeyedMutator } from 'swr';
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community, ProfileFieldConfig } from '@/types';

interface ProfileFieldSettingsProps {
  communityId: string;
  mutateCommunity: KeyedMutator<{ community: Community }>;
  profileFieldConfig: ProfileFieldConfig;
  setProfileFieldConfig: React.Dispatch<React.SetStateAction<ProfileFieldConfig>>;
}

export function ProfileFieldSettings({
  communityId,
  mutateCommunity,
  profileFieldConfig,
  setProfileFieldConfig,
}: ProfileFieldSettingsProps) {
  const saveConfig = async (newConfig: ProfileFieldConfig) => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        profileFieldConfig: newConfig,
      });
      await mutateCommunity();
      toast.success('Field settings updated');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update field settings');
    }
  };

  const handleToggle = (fieldId: string, type: 'enabled' | 'required', value: boolean) => {
    setProfileFieldConfig((prev) => {
      const updated = {
        ...prev,
        [fieldId]: {
          ...prev[fieldId],
          [type]: value,
          // If disabling, also un-require. If requiring, must be enabled.
          ...(type === 'enabled' && !value ? { required: false } : {}),
          ...(type === 'required' && value ? { enabled: true } : {}),
        },
      };
      saveConfig(updated); // Trigger save with new state
      return updated;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Profile Fields</CardTitle>
        <CardDescription>
          Configure which additional profile fields users should be asked to fill when joining or
          viewing this community.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {[
          { id: 'image', label: 'Profile Photo' },
          { id: 'dob', label: 'Date of Birth' },
          { id: 'anniversaryDate', label: 'Anniversary Date' },
          { id: 'address', label: 'Residential Address' },
          { id: 'bloodGroup', label: 'Blood Group' },
          { id: 'gender', label: 'Gender' },
          { id: 'aadhaarCard', label: 'Aadhaar Card' },
        ].map((field) => (
          <div
            key={field.id}
            className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4"
          >
            <div className="space-y-0.5">
              <Label className="text-base">{field.label}</Label>
              <p className="text-sm text-muted-foreground">
                Enable this field for your community members.
              </p>
            </div>
            {/* Actions Container: Mobile = Col, Desktop = Row Reverse (Enabled on Right, Required on Left) */}
            <div className="flex flex-col sm:flex-row-reverse items-start sm:items-center gap-4 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <Switch
                  checked={profileFieldConfig[field.id]?.enabled || false}
                  onCheckedChange={(checked) => handleToggle(field.id, 'enabled', checked)}
                />
                <Label>Enabled</Label>
              </div>
              {profileFieldConfig[field.id]?.enabled && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={profileFieldConfig[field.id]?.required || false}
                    onCheckedChange={(checked) => handleToggle(field.id, 'required', checked)}
                  />
                  <Label>Required</Label>
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
